import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { ArrowDownLeft, ArrowUp, CircleDollarSign, Flag, History, Info, SearchX } from 'lucide-react';
import { useState } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { PaginaSemPermissao } from '@/componentes/SemPermissao';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarInteiro } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDaRenovacao, type FiltroDaFrota, type LinhaDaFrota, type OrdemDaFrota } from './api';
import { JanelaDoEstudoNovo } from './JanelaDoEstudoNovo';
import {
  codigoDoEstudo,
  horas,
  idadeEscrita,
  janelaEscrita,
  NOME_DA_ORDEM,
  NOME_DO_FILTRO,
  OPCOES_POR_PAGINA_DA_FROTA,
  percentual,
  POR_PAGINA_DA_FROTA,
  reaisPorHora,
  textoDaTendencia,
  validarBuscaDaRenovacao,
  type BuscaDaRenovacao,
} from './renovacao';
import { SeloDeCandidata, SeloDoEstudo } from './Selo';

const rota = getRouteApi('/equipamentos/renovacao');

/**
 * MÓDULO 20 — FROTA › RENOVAÇÃO (0107): a lista da frota pela economia de cada
 * máquina — idade, horímetro, vida consumida, custo total por hora, a tendência
 * do custo de manutenção (só com 6 meses com horas), a margem por hora, a
 * disponibilidade e o estudo. Os 12 meses fechados, pela conta do 15.
 *
 * "Candidata a renovação" é marca A1 (sugere olhar), e só sai de um estudo com
 * os dados completos; a decisão é de quem conclui o estudo.
 *
 * Sem desenho no manual (o mapa dos módulos, p.50 a p.60, não tem o 20): segue
 * a lista com indicadores e filtros da frota (p.38, p.26), com a ação de linha
 * levando ao estudo. Dúvida de design registrada no relatório.
 */
export function PaginaRenovacao() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('renovacao.ler');
  const grava = permissoes.includes('estudo.gravar');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const [abrindo, setAbrindo] = useState(false);
  const porPagina = busca.porPagina ?? POR_PAGINA_DA_FROTA;
  const consulta = useQuery({
    queryKey: ['renovacao', 'frota', busca],
    queryFn: () => apiDaRenovacao.frota({ ...busca, porPagina }),
    enabled: pode,
    placeholderData: keepPreviousData,
    meta: { oQueLia: 'a renovação da frota' },
  });
  const f = consulta.data;

  if (sessao.data && !pode) {
    return <PaginaSemPermissao sobretitulo="Frota" titulo="Renovação da frota" modulo="a renovação da frota" licenca="renovacao.ler" />;
  }

  const ir = (mudanca: { readonly [K in keyof BuscaDaRenovacao]?: BuscaDaRenovacao[K] | undefined }, manterPagina = false) => {
    const nova = { ...busca, ...mudanca, ...(manterPagina ? {} : { pagina: undefined }) };
    void navegar({ search: validarBuscaDaRenovacao(nova as Record<string, unknown>) });
  };
  const chips: ChipFiltro[] = [
    ...(busca.busca ? [{ id: 'busca', rotulo: `Busca: ${busca.busca}`, aoRemover: () => { ir({ busca: undefined }); } }] : []),
    ...(busca.tipo ? [{ id: 'tipo', rotulo: `Tipo: ${busca.tipo}`, aoRemover: () => { ir({ tipo: undefined }); } }] : []),
    ...(busca.filtro ? [{ id: 'filtro', rotulo: NOME_DO_FILTRO[busca.filtro], aoRemover: () => { ir({ filtro: undefined }); } }] : []),
  ];
  const filtrado = chips.length > 0;
  const pagina = f?.pagina ?? busca.pagina ?? 1;
  const paginas = f ? Math.max(1, Math.ceil(f.total / f.porPagina)) : 1;
  const inicio = (pagina - 1) * porPagina;

  const colunas: readonly Coluna<LinhaDaFrota>[] = [
    {
      chave: 'maquina',
      rotulo: 'Máquina',
      prioridade: 'chave',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{l.codigo}</span>
          <TextoDaCelula texto={[l.tipo, l.modelo].filter(Boolean).join(' · ') || l.descricao} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'idade',
      rotulo: 'Idade e horímetro',
      prioridade: 'secundaria',
      celula: (l) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{idadeEscrita(l.idade)}</span>
          <span className="txt-apoio tabular-nums text-tinta-apagada">{l.horimetro === null ? 'sem leitura' : horas(l.horimetro)}</span>
        </span>
      ),
    },
    { chave: 'vida', rotulo: 'Vida', alinhamento: 'numero', celula: (l) => <span className="numero whitespace-nowrap text-tinta">{percentual(l.vidaConsumida)}</span> },
    {
      chave: 'custo',
      rotulo: 'Custo total/h',
      alinhamento: 'numero',
      celula: (l) => (
        <span className="flex flex-col items-end">
          <span className="numero whitespace-nowrap text-tinta">{reaisPorHora(l.custoTotalPorHora)}</span>
          <span className={`txt-apoio whitespace-nowrap ${l.horasInsuficientes ? 'text-atencao-texto' : 'text-tinta-apagada'}`}>{l.horasInsuficientes ? 'horas insuficientes' : horas(l.horasProdutivas)}</span>
        </span>
      ),
    },
    {
      chave: 'tendencia',
      rotulo: 'Tendência',
      celula: (l) => (
        <span className="flex min-w-0 items-center gap-1.5">
          {l.tendencia.sentido === 'sobe' ? <ArrowUp aria-hidden="true" className="size-4 shrink-0 text-parada" /> : null}
          {l.tendencia.sentido === 'desce' ? <ArrowDownLeft aria-hidden="true" className="size-4 shrink-0 text-operando" /> : null}
          <TextoDaCelula texto={textoDaTendencia(l.tendencia)} classe={`txt-apoio ${l.tendencia.valor === null ? 'text-tinta-apagada' : 'text-tinta'}`} linhas={2} />
        </span>
      ),
    },
    { chave: 'margem', rotulo: 'Margem/h', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => <span className={`numero whitespace-nowrap ${(l.margemPorHora ?? 0) < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{f?.comReceita === false ? 'sem licença' : reaisPorHora(l.margemPorHora)}</span> },
    { chave: 'disponibilidade', rotulo: 'Disponib.', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => <span className="numero whitespace-nowrap text-tinta">{percentual(l.disponibilidade)}</span> },
    {
      chave: 'estudo',
      rotulo: 'Estudo',
      celula: (l) => (
        <span className="flex flex-col items-start gap-1">
          {l.estudo ? <SeloDoEstudo situacao={l.estudo.situacao} /> : <span className="txt-apoio text-tinta-apagada">Sem estudo</span>}
          {l.candidata?.marcada ? <SeloDeCandidata /> : null}
        </span>
      ),
    },
  ];

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Frota"
          titulo="Renovação da frota"
          subtitulo={f ? `Os 12 meses fechados de ${janelaEscrita(f.janela.de, f.janela.ate)}, pela conta do custo e da receita de cada máquina` : 'A economia de cada máquina nos 12 meses fechados'}
          acoes={
            <>
              <Link to="/investimentos" className="botao botao-secundario">Plano de investimentos</Link>
              {grava ? <Botao nivel="primario" onClick={() => { setAbrindo(true); }}>Abrir estudo</Botao> : null}
            </>
          }
        />

        <div className="bloco-rebaixado flex items-start gap-3 rounded-controle p-4">
          <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-informacao" />
          <p className="txt-corpo text-pretty text-tinta">
            {f ? `${f.frases.candidata} A tendência pede ${String(f.parametros.mesesMinimosTendencia)} meses com horas, e o custo por hora, ${horas(f.parametros.horasMinimasJanela)} produtivas na janela.` : 'Candidata a renovação é uma marca para olhar a máquina, e não uma recomendação.'}
          </p>
        </div>

        {consulta.isError && !f ? null : (
          <FaixaIndicadores>
            <CardIndicador
              rotulo="Custo total por hora da frota"
              icone={CircleDollarSign}
              tom="neutro"
              carregando={!f}
              valor={f ? reaisPorHora(f.indicadores.custoTotalPorHoraDaFrota) : ''}
              contexto={f ? `${formatarInteiro(f.indicadores.comHorasSuficientes)} de ${formatarInteiro(f.indicadores.maquinas)} máquinas com horas suficientes` : 'Nos 12 meses fechados'}
            />
            <CardIndicador
              rotulo="Manutenção por hora subindo"
              icone={ArrowUp}
              tom="parada"
              carregando={!f}
              valor={f ? formatarInteiro(f.indicadores.tendenciaDeAlta) : ''}
              contexto={f ? `${formatarInteiro(f.indicadores.semTendencia)} sem tendência (menos de ${String(f.parametros.mesesMinimosTendencia)} meses)` : 'Máquinas'}
              aoAbrir={() => { ir({ filtro: 'tendencia_alta' }); }}
            />
            <CardIndicador
              rotulo="Em estudo"
              icone={History}
              tom="standby"
              carregando={!f}
              valor={f ? formatarInteiro(f.indicadores.emEstudo) : ''}
              contexto="Estudos em rascunho"
              aoAbrir={() => { ir({ filtro: 'em_estudo' }); }}
            />
            <CardIndicador
              rotulo="Candidatas a renovação"
              icone={Flag}
              tom="marca"
              carregando={!f}
              valor={f ? formatarInteiro(f.indicadores.candidatas) : ''}
              contexto="Marca dos estudos com os dados completos"
              aoAbrir={() => { ir({ filtro: 'candidatas' }); }}
            />
          </FaixaIndicadores>
        )}

        <BarraFiltros
          chips={chips}
          contagem={f ? `${formatarInteiro(f.total)} de ${formatarInteiro(f.indicadores.maquinas)} máquinas` : null}
          aoLimpar={() => { void navegar({ search: {} }); }}
        >
          <div className="min-w-0 flex-[2_1_240px]">
            <CampoTexto rotulo="Buscar máquina" valor={busca.busca ?? ''} aoMudar={(v) => { ir({ busca: v || undefined }); }} placeholder="Código, descrição ou modelo" aplicado={Boolean(busca.busca)} maxLength={60} />
          </div>
          <div className="min-w-0 flex-[1_1_200px]">
            <CampoSelecao rotulo="Tipo" valor={busca.tipo ?? ''} aoMudar={(v) => { ir({ tipo: v || undefined }); }} rotuloVazio="Todos os tipos" opcoes={(f?.tipos ?? []).map((t) => ({ valor: t, rotulo: t }))} aplicado={Boolean(busca.tipo)} />
          </div>
          <div className="min-w-0 flex-[1_1_200px]">
            <CampoSelecao rotulo="Mostrar" valor={busca.filtro ?? ''} aoMudar={(v) => { ir({ filtro: (v || undefined) as FiltroDaFrota | undefined }); }} rotuloVazio="Todas" opcoes={(Object.keys(NOME_DO_FILTRO) as FiltroDaFrota[]).map((x) => ({ valor: x, rotulo: NOME_DO_FILTRO[x] }))} aplicado={Boolean(busca.filtro)} />
          </div>
          <div className="min-w-0 flex-[1_1_200px]">
            <CampoSelecao rotulo="Ordenar por" valor={busca.ordem ?? 'custo'} aoMudar={(v) => { ir({ ordem: v === 'custo' ? undefined : (v as Exclude<OrdemDaFrota, 'custo'>) }); }} opcoes={(Object.keys(NOME_DA_ORDEM) as OrdemDaFrota[]).map((x) => ({ valor: x, rotulo: NOME_DA_ORDEM[x] }))} />
          </div>
        </BarraFiltros>

        <Tabela
          rotulo="A renovação da frota"
          colunas={colunas}
          linhas={f?.linhas ?? []}
          chaveDaLinha={(l) => l.equipamentoId}
          carregando={consulta.isPending}
          alturaDupla
          erro={consulta.isError ? <ErroDaLista oQue="As máquinas da renovação" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
          vazio={
            filtrado ? (
              <EstadoVazio
                icone={SearchX}
                titulo="Nenhuma máquina com esses filtros"
                descricao="Limpe os filtros ou escolha outro tipo de máquina."
                acao={<Botao nivel="secundario" onClick={() => { void navegar({ search: {} }); }}>Limpar filtros</Botao>}
              />
            ) : (
              <EstadoVazio titulo="Nenhuma máquina ativa na frota" descricao="Cadastre as máquinas da frota: a renovação lê o custo e as horas de cada uma." acao={permissoes.includes('equipamento.gerir') ? <Link to="/equipamentos/novo" className="botao botao-secundario">Cadastrar equipamento</Link> : undefined} />
            )
          }
          acoes={(l) =>
            l.estudo && l.estudo.situacao !== 'arquivado' ? (
              <Link to="/equipamentos/renovacao/estudos/$estudoId" params={{ estudoId: l.estudo.id }} className="botao botao-fantasma botao-p">
                {`Ver ${codigoDoEstudo(l.estudo.numero)}`}
              </Link>
            ) : null
          }
          rodape={
            <RodapeTabela
              primeiro={f && f.total > 0 ? inicio + 1 : 0}
              ultimo={f ? inicio + f.linhas.length : 0}
              total={f?.total ?? 0}
              pagina={pagina}
              paginas={paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA_DA_FROTA}
              aoMudarPagina={(nova) => { ir({ pagina: nova > 1 ? nova : undefined }, true); }}
              aoMudarPorPagina={(n) => { ir({ porPagina: n === POR_PAGINA_DA_FROTA ? undefined : n }); }}
            />
          }
        />
        {f ? <p className="txt-apoio text-pretty text-tinta-secundaria">{f.frases.valor}</p> : null}
      </div>
      {grava ? <JanelaDoEstudoNovo aberta={abrindo} aoFechar={() => { setAbrindo(false); }} /> : null}
    </div>
  );
}
